/**
 * The views that cover the page on a phone: the navigation drawer, the right
 * panel and the command palette. Each open view is a history entry with the
 * same URL, so Back closes it (design system README, "On a phone"). The
 * entry keeps the rest of the state of the page under it: ChatPage reads
 * `initialMessage` from it.
 */
export type PhoneView = "sideNav" | "panel" | "palette";

const PHONE_VIEWS: readonly PhoneView[] = ["sideNav", "panel", "palette"];

type EntryState = Record<string, unknown>;

function asEntryState(state: unknown): EntryState {
  return state !== null && typeof state === "object" ? (state as EntryState) : {};
}

/** The view that the history entry opens, or null. */
export function phoneViewOf(state: unknown): PhoneView | null {
  const entry = asEntryState(state);
  return PHONE_VIEWS.find((view) => entry[view] === true) ?? null;
}

/** The entry state with no view open. */
export function withoutPhoneView(state: unknown): EntryState {
  const next = { ...asEntryState(state) };
  for (const view of PHONE_VIEWS) delete next[view];
  return next;
}

/** The entry state with `view` open. One view is open at a time. */
export function withPhoneView(state: unknown, view: PhoneView): EntryState {
  return { ...withoutPhoneView(state), [view]: true };
}
